import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Buildings, MapPin, Clock, BookmarkSimple, Star, SealCheck, PaperPlaneTilt, Warning, ChatCircleDots } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { jobsApi, EMPLOYMENT_TYPES, WORK_MODES, EXPERIENCE_LEVELS, formatSalary, timeAgo } from "@/lib/jobsApi";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * Job Detail — public page. Shows full listing, save button, and "Apply" flow with
 * an inline cover-letter textarea. Records a view server-side on load.
 */
export default function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [coverLetter, setCoverLetter] = useState("");
  const [savedBusy, setSavedBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setJob(await jobsApi.detail(jobId)); }
    catch { toast.error("Job not found"); navigate("/jobs"); }
    finally { setLoading(false); }
  }, [jobId, navigate]);

  useEffect(() => { load(); }, [load]);

  const toggleSave = async () => {
    if (!job) return;
    setSavedBusy(true);
    try {
      if (job.is_saved) { await jobsApi.unsave(jobId); setJob({ ...job, is_saved: false }); }
      else { await jobsApi.save(jobId); setJob({ ...job, is_saved: true }); }
    } catch { toast.error("Failed"); }
    finally { setSavedBusy(false); }
  };

  const submitApplication = async () => {
    if (!user) { toast.error("Please sign in first"); return; }
    setApplying(true);
    try {
      await jobsApi.apply(jobId, { cover_letter: coverLetter });
      toast.success("Application submitted!");
      setShowApply(false);
      setJob({ ...job, has_applied: true });
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to apply";
      if (msg.toLowerCase().includes("cv")) {
        toast.error(msg);
        setTimeout(() => navigate("/career"), 1200);
      } else {
        toast.error(msg);
      }
    } finally { setApplying(false); }
  };

  if (loading) return <div className="p-8 text-white/50">Loading…</div>;
  if (!job) return null;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-6" data-testid="job-detail-page">
      <button onClick={() => navigate(-1)} data-testid="job-back" className="text-white/60 hover:text-white flex items-center gap-1 text-sm">
        <ArrowLeft size={14} weight="bold" /> Back
      </button>

      <div className="flex items-start gap-4 flex-wrap">
        {job.employer_company_logo ? (
          <img src={job.employer_company_logo} alt="" className="w-20 h-20 rounded-2xl object-cover bg-white/10" />
        ) : (
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-white/10 flex items-center justify-center">
            <Buildings size={36} className="text-white/50" weight="duotone" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-heading text-3xl md:text-4xl font-black">{job.title}</h1>
            {job.featured && (
              <span className="px-2 py-1 rounded bg-yellow-500/20 text-yellow-300 text-xs font-bold uppercase flex items-center gap-1">
                <Star size={11} weight="fill" /> Featured
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-lg text-white/70 mt-1">
            <span>{job.employer_company_name}</span>
            {job.employer?.verified && <SealCheck size={18} className="text-blue-400" weight="fill" />}
          </div>
          <div className="flex flex-wrap gap-2 mt-3 text-xs">
            <Pill icon={MapPin}>{job.location || "Location TBD"}</Pill>
            <Pill>{EMPLOYMENT_TYPES.find((t) => t.value === job.employment_type)?.label}</Pill>
            <Pill>{WORK_MODES.find((w) => w.value === job.work_mode)?.label}</Pill>
            <Pill>{EXPERIENCE_LEVELS.find((e) => e.value === job.experience_level)?.label}</Pill>
            {job.industry && <Pill>{job.industry}</Pill>}
            <Pill icon={Clock}>{timeAgo(job.created_at)}</Pill>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            data-testid="job-save-btn"
            onClick={toggleSave}
            disabled={savedBusy}
            className={`border-white/20 bg-transparent hover:bg-white/5 gap-2 ${job.is_saved ? "text-yellow-300 border-yellow-400/40" : ""}`}
          >
            <BookmarkSimple size={16} weight={job.is_saved ? "fill" : "bold"} />
            {job.is_saved ? "Saved" : "Save"}
          </Button>
        </div>
      </div>

      <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-emerald-300 font-bold">Salary</p>
          <p className="font-heading text-2xl font-black">{formatSalary(job.salary)}</p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-widest text-white/50 font-bold">Vacancies</p>
          <p className="font-heading text-2xl font-black">{job.vacancies || 1}</p>
        </div>
        {job.application_deadline && (
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-white/50 font-bold">Deadline</p>
            <p className="font-heading text-lg font-black">{new Date(job.application_deadline).toLocaleDateString()}</p>
          </div>
        )}
      </div>

      <Section title="Description">{job.description}</Section>
      {job.responsibilities && <Section title="Responsibilities">{job.responsibilities}</Section>}
      {job.qualifications && <Section title="Qualifications">{job.qualifications}</Section>}
      {job.skills?.length > 0 && (
        <Section title="Skills">
          <div className="flex flex-wrap gap-1.5">
            {job.skills.map((s) => (
              <span key={s} className="px-2.5 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-200 text-xs font-heading font-bold">
                {s}
              </span>
            ))}
          </div>
        </Section>
      )}

      {/* Apply CTA */}
      {job.is_owner ? (
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <p className="text-blue-100">This is your listing.</p>
            <Button onClick={() => navigate(`/jobs/mine/${jobId}`)} data-testid="job-view-applicants" className="bg-blue-600 hover:bg-blue-700">
              View applicants ({job.application_count || 0})
            </Button>
          </CardContent>
        </Card>
      ) : job.has_applied ? (
        <Card className="bg-emerald-500/10 border-emerald-500/30">
          <CardContent className="p-4 flex items-center gap-2">
            <SealCheck size={20} className="text-emerald-400" weight="fill" />
            <span className="text-emerald-200">You've already applied. Track status in <button className="underline" onClick={() => navigate("/jobs")}>Applications</button>.</span>
          </CardContent>
        </Card>
      ) : job.status !== "open" ? (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4 flex items-center gap-2 text-white/60">
            <Warning size={18} weight="bold" /> This job is no longer accepting applications.
          </CardContent>
        </Card>
      ) : showApply ? (
        <Card className="bg-white/[0.03] border-white/10">
          <CardHeader><CardTitle className="font-heading">Apply for this job</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-white/60">Your uploaded CV will be attached automatically. Add a short cover letter (optional).</p>
            <Textarea
              data-testid="apply-cover"
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
              placeholder="Why are you a great fit? (optional)"
              rows={5}
              maxLength={2000}
              className="bg-white/5 border-white/10"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setShowApply(false)}>Cancel</Button>
              <Button data-testid="apply-submit" onClick={submitApplication} disabled={applying} className="bg-blue-600 hover:bg-blue-700 gap-2">
                <PaperPlaneTilt size={16} weight="bold" /> {applying ? "Submitting…" : "Submit application"}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-wrap gap-2 sticky bottom-4 z-10">
          <Button data-testid="job-apply-btn" onClick={() => setShowApply(true)} className="bg-blue-600 hover:bg-blue-700 h-12 px-6 font-heading font-black text-base gap-2">
            <PaperPlaneTilt size={18} weight="bold" /> Apply now
          </Button>
          {job.application_link && (
            <a href={job.application_link} target="_blank" rel="noreferrer" className="inline-flex items-center px-6 h-12 rounded-md border border-white/20 hover:bg-white/5 text-white/80 font-heading font-bold">
              External link
            </a>
          )}
          <Button variant="ghost" onClick={() => navigate(`/career/${job.employer_id}`)} data-testid="job-employer-view" className="text-white/70 gap-2">
            <Buildings size={16} weight="bold" /> Employer profile
          </Button>
          {job.contact_email && (
            <a href={`mailto:${job.contact_email}`} className="inline-flex items-center gap-1 px-4 h-12 text-white/60 hover:text-white text-sm">
              <ChatCircleDots size={16} weight="bold" /> Contact
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function Pill({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-white/70 text-xs">
      {Icon && <Icon size={12} weight="bold" />} {children}
    </span>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-heading text-sm uppercase tracking-widest text-white/50 font-black">{title}</h2>
      <div className="mt-2 text-white/85 whitespace-pre-line leading-relaxed text-[15px]">{children}</div>
    </div>
  );
}
